import { useEffect, useReducer } from 'react';

import * as SlackApi from '../domains/slack/api';
import type { Channel } from '../domains/slack/api/common';

enum ACTIONS {
  SET_ALL_CHANNELS,
  SET_CHANNELS,
  SET_HAS_ERROR,
  SET_HAS_API_ERROR,
  SET_CONNECTION_STATE,
  SET_IS_ENABLED,
  TOGGLE_MODAL_OPEN,
  SET_IS_LOADING,
  ON_CONNECT,
}

enum CONNECTION_STATE {
  UNKNOWN,
  CONNECTED,
  NOT_CONNECTED,
}

type Action =
  | { type: ACTIONS.SET_ALL_CHANNELS; payload: Array<Channel> }
  | { type: ACTIONS.SET_CHANNELS; payload: Array<Channel> }
  | { type: ACTIONS.SET_HAS_ERROR; payload: boolean }
  | { type: ACTIONS.SET_HAS_API_ERROR; payload: boolean }
  | { type: ACTIONS.SET_CONNECTION_STATE; payload: CONNECTION_STATE }
  | { type: ACTIONS.SET_IS_ENABLED; payload: boolean }
  | { type: ACTIONS.TOGGLE_MODAL_OPEN }
  | { type: ACTIONS.SET_IS_LOADING; payload: boolean }
  | { type: ACTIONS.ON_CONNECT };

interface State {
  allChannels: Array<Channel>;
  channels: Array<Channel>;
  hasApiError: boolean;
  hasError: boolean;
  connectionState: CONNECTION_STATE;
  isLoading: boolean;
  isEnabled: boolean;
  isModalOpen: boolean;
}

type IsConnected = boolean | null;

export interface Settings {
  channels: Array<Channel>;
  enabled: boolean;
  valid: boolean;
  dirty: boolean;
  isConnected: IsConnected;
}

const isSlackValid = (slackChannels: Array<Channel>) => slackChannels && slackChannels.length > 0;

function reducer(state: State, action: Action) {
  switch (action.type) {
    case ACTIONS.SET_ALL_CHANNELS:
      return { ...state, allChannels: action.payload };
    case ACTIONS.SET_CHANNELS:
      return { ...state, channels: action.payload, hasError: !isSlackValid(action.payload) };
    case ACTIONS.SET_HAS_ERROR:
      return { ...state, hasError: !isSlackValid(state.channels) };
    case ACTIONS.SET_HAS_API_ERROR:
      return { ...state, hasApiError: true };
    case ACTIONS.SET_CONNECTION_STATE:
      return { ...state, connectionState: action.payload };
    case ACTIONS.SET_IS_ENABLED:
      return { ...state, isEnabled: action.payload };
    case ACTIONS.TOGGLE_MODAL_OPEN:
      return { ...state, isModalOpen: !state.isModalOpen };
    case ACTIONS.SET_IS_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTIONS.ON_CONNECT:
      return {
        ...state,
        connectionState: CONNECTION_STATE.CONNECTED,
        isEnabled: true,
        isModalOpen: false,
      };
    default:
      return state;
  }
}

function mapConnectionStateToIsConnected(connectionState: CONNECTION_STATE): IsConnected {
  switch (connectionState) {
    case CONNECTION_STATE.CONNECTED:
      return true;
    case CONNECTION_STATE.NOT_CONNECTED:
      return false;
    case CONNECTION_STATE.UNKNOWN:
    default:
      return null;
  }
}

interface Props {
  settings: Settings;
  onChange(settings: Settings): void;
  siteId: number | undefined;
  isMinimalPlan: boolean;
}

export function useSlackState({ settings, onChange, siteId, isMinimalPlan }: Props): {
  state: Omit<State, 'connectionState'> & { isConnected: IsConnected };
  actions: {
    toggleModalOpen(): void;
    setChannels(channels: Channel[]): void;
    setHasError(hasError: boolean): void;
    setIsEnabled(isEnabled: boolean): void;
    setIsConnecting(): void;
  };
} {
  const { dirty } = settings;
  const [state, dispatch] = useReducer(reducer, {
    allChannels: [],
    channels: settings.channels,
    hasError: dirty && !settings.valid,
    hasApiError: false,
    connectionState: CONNECTION_STATE.UNKNOWN,
    isEnabled: settings.enabled,
    isLoading: false,
    isModalOpen: false,
  });
  const { channels, connectionState, isEnabled } = state;

  useEffect(() => {
    dispatch({ type: ACTIONS.SET_CONNECTION_STATE, payload: CONNECTION_STATE.UNKNOWN });
  }, [siteId]);

  useEffect(() => {
    if (!isMinimalPlan) {
      return;
    }

    if (connectionState === CONNECTION_STATE.CONNECTED && siteId) {
      dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

      SlackApi.getChannels(siteId)
        .then(({ data }) => {
          dispatch({ type: ACTIONS.SET_ALL_CHANNELS, payload: data.channels });
        })
        .catch(() => {
          dispatch({ type: ACTIONS.SET_HAS_API_ERROR, payload: false });
        })
        .finally(() => {
          dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        });
    }

    if (connectionState === CONNECTION_STATE.UNKNOWN && siteId) {
      dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

      SlackApi.getConnectionStatus(siteId)
        .then(() => {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATE, payload: CONNECTION_STATE.CONNECTED });
        })
        .catch(() => {
          dispatch({ type: ACTIONS.SET_CONNECTION_STATE, payload: CONNECTION_STATE.NOT_CONNECTED });
        })
        .finally(() => {
          dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        });
    }
  }, [siteId, isMinimalPlan, connectionState]);

  useEffect(() => {
    if (dirty && !isSlackValid(channels)) {
      dispatch({ type: ACTIONS.SET_HAS_ERROR, payload: false });
    }
  }, [dirty, channels]);

  useEffect(() => {
    if (connectionState !== CONNECTION_STATE.UNKNOWN) {
      // TODO: do not call the callback when external state changes
      // When used on Edit-Create form, new settings may come from the outside world, ex.
      // default ones when the segment ID becomes null (segment is not yet created).
      // This callback is called always when `isEnabled` and `channels` change, no matter if
      // that happens because of internal or external change; Possibly we would need to add
      // segmentId here
      onChange({
        channels,
        enabled: isEnabled,
        valid: isSlackValid(channels),
        dirty,
        isConnected: mapConnectionStateToIsConnected(connectionState),
      });
    }
  }, [onChange, channels, isEnabled, dirty, connectionState]);

  return {
    state: {
      ...state,
      isConnected: mapConnectionStateToIsConnected(connectionState),
    },
    actions: {
      toggleModalOpen() {
        dispatch({ type: ACTIONS.TOGGLE_MODAL_OPEN });
      },
      setIsEnabled(payload) {
        dispatch({ type: ACTIONS.SET_IS_ENABLED, payload });
      },
      setChannels(payload) {
        dispatch({ type: ACTIONS.SET_CHANNELS, payload });
      },
      setHasError(payload) {
        dispatch({ type: ACTIONS.SET_HAS_ERROR, payload });
      },
      setIsConnecting() {
        dispatch({ type: ACTIONS.ON_CONNECT });
      },
    },
  };
}
